/**
 * Product Form V2 - JavaScript avec BEM
 * Nouvelle logique de synchronisation variantes/selling plans
 */

class ProductFormV2 {
  constructor(container) {
    if (!container) return;

    this.container = container;
    this.form = container.querySelector('form[action*="/cart/add"]');

    if (!this.form) return;

    // Elements du formulaire
    this.submitButton = container.querySelector('.product-form__submit');
    this.variantInput = this.form.querySelector('input[name="id"]');
    this.quantityInput = this.form.querySelector('input[name="quantity"]');
    this.sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
    this.errorMessage = container.querySelector('[data-error-message]');

    // Nouveaux sélecteurs pour V2
    this.variantRadios = container.querySelectorAll('[name^="variant-"]');
    this.frequencyRadios = container.querySelectorAll('[name^="purchase-type-"]');
    this.sellingPlanRadios = container.querySelectorAll('[name^="selling-plan-"]');
    this.frequencyOptions = container.querySelectorAll('.product-form__frequency-option');

    // Éléments de prix et textes dynamiques
    this.oneTimePriceDisplay = container.querySelector('[data-one-time-price]');
    this.oneTimeComparePriceDisplay = container.querySelector('[data-one-time-compare-price]');
    this.subscriptionPriceDisplay = container.querySelector('[data-subscription-price]');
    this.subscriptionComparePriceDisplay = container.querySelector('[data-subscription-compare-price]');
    this.subscriptionBadge = container.querySelector('[data-subscription-badge]');
    this.subscriptionTitle = container.querySelector('[data-subscription-title]');
    this.deliveryNote = container.querySelector('[data-delivery-note]');
    this.buttonPriceSpan = this.submitButton?.querySelector('[data-button-price]');
    this.buttonComparePriceSpan = this.submitButton?.querySelector('[data-button-compare-price]');

    // Paramètres Sova Days
    this.sovaDaysEnabled = container.dataset.sovaDaysEnabled === 'true';
    this.sovaDaysDiscount = parseFloat(container.dataset.sovaDaysDiscount) || 0;
    this.sovaDaysAdditionalAmount = parseFloat(container.dataset.sovaDaysAdditionalAmount) || 0;
    this.subscriptionMessage = container.dataset.subscriptionMessage || '';
    this.sovaDaysBadge = container.querySelector('[data-sova-days-badge]');

    // Paramètres badge format
    this.subscriptionBadgeFormat = container.dataset.subscriptionBadgeFormat || 'percentage';
    this.savingsText = container.dataset.savingsText || 'économisés';

    // Sticky Button Elements
    this.stickyCtaContainer = document.querySelector('[data-sticky-cta]');
    this.stickySubmitButton = document.querySelector('[data-sticky-submit]');
    this.stickyLabel = document.querySelector('[data-sticky-label]');
    this.stickyPrice = document.querySelector('[data-sticky-price]');
    this.stickyComparePrice = document.querySelector('[data-sticky-compare-price]');

    this.init();
  }

  init() {
    this.bindEvents();
    this.handleVariantChange(); // Initialiser avec la variante sélectionnée
    this.replacePricePlaceholder();
    this.initStickyButton();
  }

  bindEvents() {
    // Changement de variante (Durée de la cure)
    this.variantRadios.forEach(radio => {
      radio.addEventListener('change', () => this.handleVariantChange());
    });

    // Changement de type d'achat (Achat unique / Abonnement)
    this.frequencyRadios.forEach(radio => {
      radio.addEventListener('change', () => this.handleFrequencyChange());
    });

    // Soumission du formulaire
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  /**
   * Gestion du changement de variante
   * Logique: première variante → premier selling plan, sinon → deuxième selling plan
   */
  handleVariantChange() {
    const selectedVariant = this.container.querySelector('[name^="variant-"]:checked');
    if (!selectedVariant) return;

    const variantId = selectedVariant.dataset.variantId;
    const variantIndex = parseInt(selectedVariant.dataset.variantIndex);
    const variantPrice = parseInt(selectedVariant.dataset.variantPrice);
    const variantComparePrice = parseInt(selectedVariant.dataset.variantComparePrice) || 0;

    // Mettre à jour l'input hidden de variante
    if (this.variantInput && variantId) {
      this.variantInput.value = variantId;
    }

    // Quantité toujours à 1
    if (this.quantityInput) {
      this.quantityInput.value = 1;
    }

    // Toujours mettre à jour les prix d'achat unique
    this.updateOneTimePriceDisplay(variantPrice, variantComparePrice);

    // Gérer les selling plans s'ils existent
    const sellingPlans = Array.from(this.sellingPlanRadios);

    if (sellingPlans.length > 0) {
      // Déterminer quel selling plan sélectionner
      // Si première variante (index 0) → premier plan, sinon → deuxième plan
      const sellingPlanIndex = variantIndex === 0 ? 0 : 1;

      if (sellingPlans[sellingPlanIndex]) {
        sellingPlans[sellingPlanIndex].checked = true;

        // Récupérer les données du selling plan sélectionné
        const selectedPlan = sellingPlans[sellingPlanIndex];
        const discountPercent = parseInt(selectedPlan.dataset.discountPercent) || 0;
        const planName = selectedPlan.dataset.sellingPlanName || '';

        // Calculer le prix subscription
        const subscriptionPrice = Math.round(variantPrice * (1 - discountPercent / 100));

        // Mettre à jour les prix d'abonnement
        this.updateSubscriptionDisplay(variantPrice, variantComparePrice, subscriptionPrice, discountPercent, planName);

        // Mettre à jour le selling_plan input si abonnement est sélectionné
        const isSubscriptionSelected = this.container.querySelector('[name^="purchase-type-"]:checked')?.value === 'subscription';
        if (isSubscriptionSelected && this.sellingPlanInput) {
          this.sellingPlanInput.value = selectedPlan.dataset.sellingPlanId;
        }
      }
    }

    // Mettre à jour le prix du bouton
    this.updateButtonPrice();
  }

  /**
   * Gestion du changement de type d'achat (Achat unique / Abonnement)
   */
  handleFrequencyChange() {
    const selectedFrequency = this.container.querySelector('[name^="purchase-type-"]:checked');
    if (!selectedFrequency) return;

    const purchaseType = selectedFrequency.value;

    // Gérer les classes --selected sur les options
    this.frequencyOptions.forEach(option => {
      if (option.dataset.option === purchaseType) {
        option.classList.add('product-form__frequency-option--selected');
      } else {
        option.classList.remove('product-form__frequency-option--selected');
      }
    });

    // Mettre à jour le selling_plan input
    if (purchaseType === 'one-time') {
      // Achat unique : vider le selling_plan
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = '';
      }
    } else if (purchaseType === 'subscription') {
      // Abonnement : mettre le selling_plan sélectionné
      const selectedPlan = this.container.querySelector('[name^="selling-plan-"]:checked');
      if (selectedPlan && this.sellingPlanInput) {
        this.sellingPlanInput.value = selectedPlan.dataset.sellingPlanId;
      }
    }

    this.updateButtonPrice();
  }

  /**
   * Met à jour l'affichage du prix d'achat unique
   * Affiche le prix d'origine le plus bas (compare_price ou prix avant Sova Days)
   */
  updateOneTimePriceDisplay(price, comparePrice) {
    // Appliquer la réduction Sova Days sur le prix final
    const discountedPrice = this.applySovaDaysDiscount(price);
    const hasSovaDaysDiscount = this.sovaDaysEnabled && this.sovaDaysDiscount > 0 && discountedPrice < price;

    // Prix final (toujours affiché)
    if (this.oneTimePriceDisplay && price) {
      this.oneTimePriceDisplay.textContent = this.formatPrice(discountedPrice);
    }

    // Prix d'origine le plus bas (compare_price ou prix avant Sova Days)
    if (this.oneTimeComparePriceDisplay) {
      if (comparePrice && comparePrice > 0 && comparePrice > discountedPrice) {
        // Afficher compare_price s'il existe et est supérieur au prix final
        this.oneTimeComparePriceDisplay.textContent = this.formatPrice(comparePrice);
        this.oneTimeComparePriceDisplay.style.display = '';
      } else if (hasSovaDaysDiscount) {
        // Sinon, afficher le prix avant Sova Days si réduction active
        this.oneTimeComparePriceDisplay.textContent = this.formatPrice(price);
        this.oneTimeComparePriceDisplay.style.display = '';
      } else {
        this.oneTimeComparePriceDisplay.style.display = 'none';
      }
    }
  }

  /**
   * Met à jour l'affichage de l'abonnement
   * Affiche le prix d'origine le plus bas (compare_price ou prix avant Sova Days)
   */
  updateSubscriptionDisplay(variantPrice, variantComparePrice, subscriptionPrice, discountPercent, planName) {
    // Appliquer la réduction Sova Days sur le prix d'abonnement
    const discountedSubscriptionPrice = this.applySovaDaysDiscount(subscriptionPrice);
    const hasSovaDaysDiscount = this.sovaDaysEnabled && this.sovaDaysDiscount > 0 && discountedSubscriptionPrice < subscriptionPrice;

    // Prix final d'abonnement (toujours affiché)
    if (this.subscriptionPriceDisplay) {
      this.subscriptionPriceDisplay.textContent = this.formatPrice(discountedSubscriptionPrice);
    }

    // Prix d'origine le plus bas (compare_price ou prix d'abonnement avant Sova Days)
    if (this.subscriptionComparePriceDisplay) {
      if (variantComparePrice && variantComparePrice > 0 && variantComparePrice > discountedSubscriptionPrice) {
        // Afficher compare_price s'il existe et est supérieur au prix final
        this.subscriptionComparePriceDisplay.textContent = this.formatPrice(variantComparePrice);
        this.subscriptionComparePriceDisplay.style.display = '';
      } else if (hasSovaDaysDiscount) {
        // Sinon, afficher le prix d'abonnement avant Sova Days si réduction active
        this.subscriptionComparePriceDisplay.textContent = this.formatPrice(subscriptionPrice);
        this.subscriptionComparePriceDisplay.style.display = '';
      } else {
        this.subscriptionComparePriceDisplay.style.display = 'none';
      }
    }

    // Badge de réduction
    let totalDiscountPercent = discountPercent;
    let totalDiscountAmount = 0;
    if (this.subscriptionBadge) {
      // Calculer le pourcentage de réduction par rapport au compare_price (si existe) ou au variantPrice
      const basePrice = variantComparePrice && variantComparePrice > 0 ? variantComparePrice : variantPrice;
      totalDiscountPercent = Math.round((1 - (discountedSubscriptionPrice / basePrice)) * 100);
      totalDiscountAmount = basePrice - discountedSubscriptionPrice;

      if (totalDiscountPercent > 0) {
        if (this.subscriptionBadgeFormat === 'money') {
          this.subscriptionBadge.textContent = `${this.formatPrice(totalDiscountAmount)} ${this.savingsText}`;
        } else {
          this.subscriptionBadge.textContent = `-${totalDiscountPercent}%`;
        }
      }
    }

    // Titre de l'abonnement (nom du plan)
    if (this.subscriptionTitle && planName) {
      // Sauvegarder l'icône si elle existe
      const infoIcon = this.subscriptionTitle.querySelector('.product-form__frequency-info-icon');

      // Mettre à jour le texte
      this.subscriptionTitle.textContent = planName;

      // Réajouter l'icône si elle existait
      if (infoIcon) {
        this.subscriptionTitle.appendChild(infoIcon);
      }
    }

    // Mettre à jour le badge Sova Days avec les bons pourcentages
    // Premier pourcentage = totalDiscountPercent (affiché dans le badge)
    // Deuxième pourcentage = discountPercent (réduction de base de la subscription)
    // Badge Sova Days est TOUJOURS en pourcentage
    this.updateSovaDaysBadge(totalDiscountPercent, discountPercent);
  }

  /**
   * Met à jour le badge Sova Days (TOUJOURS EN POURCENTAGE)
   * @param {number} totalDiscountPercent - Pourcentage total affiché dans le badge (incluant Sova Days)
   * @param {number} baseDiscountPercent - Pourcentage de réduction de base de la subscription
   */
  updateSovaDaysBadge(totalDiscountPercent, baseDiscountPercent) {
    if (!this.sovaDaysBadge || !this.subscriptionMessage || !this.sovaDaysEnabled) {
      return;
    }

    // Remplacer [reduction] par le pourcentage total (celui du badge)
    let message = this.subscriptionMessage.replace('[reduction]', totalDiscountPercent);

    // Remplacer -10% par le pourcentage de base de la subscription
    const discountText = `-${baseDiscountPercent}%`;
    message = message.replace('-10%', discountText);

    // Mettre à jour le badge
    this.sovaDaysBadge.textContent = message;
  }

  /**
   * Met à jour le prix dans le bouton CTA
   */
  updateButtonPrice() {
    if (!this.buttonPriceSpan) return;

    const selectedPurchaseType = this.container.querySelector('[name^="purchase-type-"]:checked');
    const selectedVariant = this.container.querySelector('[name^="variant-"]:checked');
    const variantPrice = parseInt(selectedVariant?.dataset.variantPrice) || 0;
    const variantComparePrice = parseInt(selectedVariant?.dataset.variantComparePrice) || 0;
    let priceToShow = 0;

    if (selectedPurchaseType?.value === 'subscription') {
      // Abonnement : prix calculé avec réduction d'abonnement + réduction Sova Days
      const selectedPlan = this.container.querySelector('[name^="selling-plan-"]:checked');
      const discountPercent = parseInt(selectedPlan?.dataset.discountPercent) || 0;
      const subscriptionPrice = Math.round(variantPrice * (1 - discountPercent / 100));
      priceToShow = this.applySovaDaysDiscount(subscriptionPrice);
    } else {
      // Achat unique (par défaut si aucun type sélectionné ou si one-time sélectionné)
      priceToShow = this.applySovaDaysDiscount(variantPrice);
    }

    if (priceToShow > 0) {
      const formattedPrice = this.formatPrice(priceToShow);
      this.buttonPriceSpan.textContent = formattedPrice;

      // Gérer l'affichage du compare price
      if (this.buttonComparePriceSpan) {
        if (variantComparePrice && variantComparePrice > variantPrice) {
          const formattedComparePrice = this.formatPrice(variantComparePrice);
          this.buttonComparePriceSpan.innerHTML = `&nbsp;<s>${formattedComparePrice}</s>`;
          this.buttonComparePriceSpan.style.display = '';
        } else {
          this.buttonComparePriceSpan.style.display = 'none';
        }
      }

      // Mettre à jour aussi le sticky button avec le compare price
      let formattedComparePrice = null;
      if (variantComparePrice && variantComparePrice > variantPrice) {
        formattedComparePrice = this.formatPrice(variantComparePrice);
      }
      this.updateStickyButton(null, formattedPrice, formattedComparePrice);
    }
  }

  /**
   * Gestion de la soumission du formulaire
   */
  async handleSubmit(e) {
    e.preventDefault();

    // Désactiver le bouton pendant le traitement
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('product-form__submit--loading');
    }

    // Masquer les erreurs précédentes
    this.hideError();

    try {
      // Récupérer les données du formulaire
      const formData = new FormData(this.form);

      // Ajouter au panier via l'API Shopify
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erreur lors de l\'ajout au panier');
      }

      const item = await response.json();

      // Succès : déclencher un événement personnalisé
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item }
      }));

      // Rafraîchir et ouvrir le cart drawer
      const cartDrawer = document.querySelector('apt-cart-drawer');
      if (cartDrawer) {
        // Rafraîchir le contenu du panier
        if (typeof cartDrawer.refresh === 'function') {
          await cartDrawer.refresh();
        } else {
          // Dispatcher l'événement refresh pour forcer le rafraîchissement
          cartDrawer.dispatchEvent(new CustomEvent('refresh'));
        }

        // Petit délai pour laisser le temps au refresh de se terminer
        await new Promise(resolve => setTimeout(resolve, 100));

        // Ouvrir le drawer
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          cartDrawer.classList.add('active');
        }
      }

      // Fermer la modal quick-buy si on est dedans
      const quickBuyModal = this.container.closest('apt-quick-buy-drawer');
      if (quickBuyModal && typeof quickBuyModal.close === 'function') {
        quickBuyModal.close();
        console.log('🔒 Quick buy modal closed after add to cart');
      }

      // Rafraîchir le nombre d'articles dans le panier
      this.refreshCartCount();

      // Feedback visuel de succès
      this.showSuccess();

    } catch (error) {
      this.showError(error.message);
    } finally {
      // Réactiver le bouton
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('product-form__submit--loading');
      }
    }
  }

  /**
   * Rafraîchit le compteur du panier
   */
  async refreshCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Mettre à jour tous les compteurs de panier
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du panier:', error);
    }
  }

  /**
   * Affiche un message de succès
   */
  showSuccess() {
    if (this.submitButton) {
      const originalText = this.submitButton.innerHTML;
      this.submitButton.innerHTML = '<span>✓ AJOUTÉ AU PANIER</span>';
      this.submitButton.classList.add('product-form__submit--success');

      setTimeout(() => {
        this.submitButton.innerHTML = originalText;
        this.submitButton.classList.remove('product-form__submit--success');
      }, 2000);
    }
  }

  /**
   * Affiche un message d'erreur
   */
  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.style.display = 'block';
    }
  }

  /**
   * Masque le message d'erreur
   */
  hideError() {
    if (this.errorMessage) {
      this.errorMessage.style.display = 'none';
      this.errorMessage.textContent = '';
    }
  }

  /**
   * Formate un prix en centimes avec la devise du site
   */
  formatPrice(priceInCents) {
    const amount = priceInCents / 100;
    const formatted = amount.toFixed(2).replace(/\.00$/, '');

    // Récupérer la devise du site
    const currency = window.apotech?.currency || 'EUR';

    // Symboles de devises
    const currencySymbols = {
      'EUR': '€',
      'USD': '$',
      'GBP': '£',
      'CHF': 'CHF',
      'CAD': 'CA$'
    };

    const symbol = currencySymbols[currency] || currency;

    // Format selon la locale
    const localizedAmount = currency === 'EUR'
      ? formatted.replace('.', ',')
      : formatted;

    return `${symbol}${localizedAmount}`;
  }

  /**
   * Applique la réduction Sova Days à un prix
   * Additionne le pourcentage global et le pourcentage supplémentaire du produit
   */
  applySovaDaysDiscount(priceInCents) {
    if (!this.sovaDaysEnabled) {
      return priceInCents;
    }

    // Calculer le pourcentage total de réduction
    const totalDiscountPercent = this.sovaDaysDiscount + this.sovaDaysAdditionalAmount;

    if (totalDiscountPercent <= 0) {
      return priceInCents;
    }

    // Appliquer la réduction totale
    const discountMultiplier = 1 - (totalDiscountPercent / 100);
    const discountedPrice = Math.round(priceInCents * discountMultiplier);

    // S'assurer que le prix ne devient pas négatif
    return Math.max(0, discountedPrice);
  }

  /**
   * Remplace [price] par le plus petit prix divisé par 3
   */
  replacePricePlaceholder() {
    const deliveryInfoItems = this.container.querySelectorAll('.product-form__delivery-info-item');
    if (deliveryInfoItems.length === 0) return;

    // Récupérer tous les prix des variantes
    const prices = [];
    this.variantRadios.forEach(radio => {
      const price = parseInt(radio.dataset.variantPrice);
      if (!isNaN(price) && price > 0) {
        prices.push(price);
      }
    });

    if (prices.length === 0) return;

    // Trouver le plus petit prix
    const minPrice = Math.min(...prices);

    // Appliquer la réduction Sova Days
    const discountedMinPrice = this.applySovaDaysDiscount(minPrice);

    // Diviser par 3 et arrondir
    const priceDiv3 = Math.round(discountedMinPrice / 3);

    // Formater le prix
    const formattedPrice = this.formatPrice(priceDiv3);

    // Remplacer [price] dans tous les éléments
    deliveryInfoItems.forEach(item => {
      const textElements = item.querySelectorAll('p, strong, span');
      textElements.forEach(el => {
        if (el.innerHTML.includes('[price]')) {
          el.innerHTML = el.innerHTML.replace(/\[price\]/g, formattedPrice);
        }
      });
    });
  }

  /**
   * Initialise le sticky button qui apparaît en bas de la page sur mobile
   * jusqu'à ce que le vrai bouton soit visible
   */
  initStickyButton() {
    if (!this.submitButton || !this.stickyCtaContainer || !this.stickySubmitButton) return;

    // Click sur le sticky button déclenche le vrai bouton
    this.stickySubmitButton.addEventListener('click', () => {
      this.submitButton.click();
    });

    // Intersection Observer pour détecter quand le vrai bouton est visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Le vrai bouton est visible, cacher le sticky button
          this.stickyCtaContainer.classList.remove('product-form__sticky-cta--active');
        } else {
          // Le vrai bouton n'est pas visible, afficher le sticky button
          this.stickyCtaContainer.classList.add('product-form__sticky-cta--active');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px'
    });

    // Observer le vrai bouton
    observer.observe(this.submitButton);
  }

  /**
   * Met à jour le contenu du sticky button (label, prix et compare price)
   */
  updateStickyButton(label, price, comparePrice = null) {
    if (!this.stickyLabel || !this.stickyPrice) return;

    if (label) {
      this.stickyLabel.textContent = label;
    }

    if (price) {
      this.stickyPrice.textContent = price;
    }

    // Gérer l'affichage du compare price dans le sticky button
    if (this.stickyComparePrice) {
      if (comparePrice) {
        this.stickyComparePrice.innerHTML = `&nbsp;<s>${comparePrice}</s>`;
        this.stickyComparePrice.style.display = '';
      } else {
        this.stickyComparePrice.style.display = 'none';
      }
    }
  }
}

/**
 * Calcule la date de livraison en jours ouvrés
 */
function calculateDeliveryDate(businessDays) {
  let date = new Date();
  let daysAdded = 0;

  while (daysAdded < businessDays) {
    date.setDate(date.getDate() + 1);
    const dayOfWeek = date.getDay();

    // Ignorer samedi (6) et dimanche (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysAdded++;
    }
  }

  return date;
}

/**
 * Formate une date selon la langue
 */
function formatDeliveryDate(date, language = 'fr') {
  const translations = {
    fr: {
      dayNames: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
      monthNames: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
    },
    en: {
      dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    },
    it: {
      dayNames: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
      monthNames: ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre']
    }
  };

  const lang = translations[language] || translations['fr'];
  const dayName = lang.dayNames[date.getDay()];
  const day = date.getDate();
  const month = lang.monthNames[date.getMonth()];

  return `${dayName} ${day} ${month}`;
}

/**
 * Initialise les dates de livraison
 */
function initDeliveryDate() {
  const deliveryElements = document.querySelectorAll('[data-delivery-date]');

  deliveryElements.forEach(element => {
    const businessDays = parseInt(element.dataset.deliveryDays) || 5;
    const language = element.dataset.deliveryLanguage || 'fr';
    const deliveryDate = calculateDeliveryDate(businessDays);
    const formattedDate = formatDeliveryDate(deliveryDate, language);
    element.textContent = formattedDate;
  });
}

/**
 * Initialisation
 */
const productFormInstances = [];

function initProductForms() {
  const productForms = document.querySelectorAll('.product-form');

  productForms.forEach(form => {
    const instance = new ProductFormV2(form);
    productFormInstances.push(instance);
  });

  initDeliveryDate();
}

// Lancer l'initialisation
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductForms);
} else {
  initProductForms();
}

// Support de l'éditeur de thème Shopify
if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const productForms = event.target.querySelectorAll('.product-form');
    productForms.forEach(form => new ProductFormV2(form));
  });
}

// Exporter ProductFormV2 globalement pour utilisation dans quick-buy-modal
window.ProductFormV2 = ProductFormV2;
